import type {
  ProjectCommitSnapshot,
  VersionWorkspaceMode,
  VersionWorkspaceCreationResult,
  VersionWorkspaceService,
  FetchProjectCommitRecordsOptions,
} from '@/services/version-workspace';
import type { useVersionWorkspaceStore } from '@/stores';
import { logger } from '@/utils';

/**
 * 历史版本重载请求上下文。
 */
export interface VersionWorkspaceReloadContext {
  /** 当前异步重载是否仍属于最新一次版本切换请求。 */
  isCurrent: () => boolean;
}

/**
 * 版本工作区控制器依赖。
 *
 * Controller 只负责编排版本服务、状态 store 与编辑器重载回调，不直接触碰 Viewer、
 * 设计树组件或任何 UI 组件，避免历史版本预览流程和 CAD 核心生命周期耦合。
 */
export interface UseVersionWorkspaceControllerOptions {
  /** 版本工作区 UI 状态 store。 */
  store: ReturnType<typeof useVersionWorkspaceStore>;
  /** 版本工作区服务，当前阶段可由 mock service 提供。 */
  service: VersionWorkspaceService;
  /** 读取当前项目 ID，避免 controller 绑定路由实现。 */
  getProjectId: () => string;
  /** 从编辑态切到历史版本前的风险确认。 */
  confirmSwitchFromEditing: () => Promise<boolean>;
  /** 重载历史提交快照对应的中间模型与设计树。 */
  reloadCommitSnapshot: (
    snapshot: ProjectCommitSnapshot,
    context: VersionWorkspaceReloadContext,
  ) => Promise<void>;
  /** 重载当前可编辑工作区。 */
  reloadEditableWorkspace: (workspace: VersionWorkspaceCreationResult) => Promise<void>;
  /** 同步当前可编辑提交对应的可刷新入口，例如 OpenProject 路由 query。 */
  syncEditableCommitRoute?: (commitId: string) => Promise<void>;
  /** 成功提示出口。 */
  notifySuccess: (message: string) => void;
  /** 失败提示出口。 */
  notifyError: (message: string) => void;
  /** 提交选择防抖时间，测试可传 0 以避免等待真实计时器。 */
  switchDebounceMs?: number;
}

interface ScheduledSwitchCommit {
  /** 本次调度的提交 ID。 */
  commitId: string;
  /** 本次调度成为最新 owner 时分配的请求编号。 */
  requestId: number;
  /** 结束该次 selectCommit 调用等待的 resolver。 */
  resolve: () => void;
  /** 当前 selectCommit 调用向外返回的 Promise。 */
  promise: Promise<void>;
}

interface VersionWorkspaceModeSnapshot {
  /** 切换前的工作区模式。 */
  readonly mode: VersionWorkspaceMode;
  /** 切换前选中的历史提交 ID。 */
  readonly selectedCommitId: string | null;
}

interface SwitchCommitNowOptions {
  /** 是否在拉取快照前立即进入只读预览态。 */
  readonly enterPreviewBeforeLoad?: boolean;
  /** 失败时需要恢复的旧状态；缺省时保持当前状态。 */
  readonly restoreStateOnFailure?: VersionWorkspaceModeSnapshot;
}

/**
 * 提交记录加载入口选项。
 */
export interface LoadCommitRecordsOptions {
  /** 是否跳过本地提交记录缓存并强制重新请求后端提交记录。 */
  readonly forceRefresh?: boolean;
}

/**
 * 版本工作区控制器。
 *
 * @param options 控制器依赖。
 * @returns 提交记录加载、历史预览切换和基于历史提交创建工作区的编排方法。
 */
export function useVersionWorkspaceController(options: UseVersionWorkspaceControllerOptions) {
  const {
    store,
    service,
    getProjectId,
    confirmSwitchFromEditing,
    reloadCommitSnapshot,
    reloadEditableWorkspace,
    syncEditableCommitRoute,
    notifySuccess,
    notifyError,
    switchDebounceMs = 160,
  } = options;
  let isDisposed = false;
  let commitRecordsRequestId = 0;
  let commitRecordsAbortController: AbortController | null = null;
  let switchCommitRequestId = 0;
  let switchCommitAbortController: AbortController | null = null;
  let createWorkspaceRequestId = 0;
  let pendingSwitchCommitId: string | null = null;
  let switchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let scheduledSwitchCommit: ScheduledSwitchCommit | null = null;
  let editingSwitchConfirmPromise: Promise<boolean> | null = null;
  let hasConfirmedEditingSwitch = false;

  /**
   * 判断提交记录请求结果是否仍属于当前 controller 生命周期。
   *
   * @param requestId 本次提交记录请求编号。
   * @returns 请求仍有效时返回 true。
   */
  function isActiveCommitRecordsRequest(requestId: number): boolean {
    return !isDisposed && commitRecordsRequestId === requestId;
  }

  /**
   * 中止当前提交记录加载请求。
   *
   * 设计说明：requestId 负责阻止旧结果回写，AbortSignal 负责释放已过期的网络请求；
   * 二者配合才能同时保证 UI 状态正确和后端资源不被无效请求占用。
   */
  function abortCommitRecordsRequest(): void {
    if (!commitRecordsAbortController) {
      return;
    }
    commitRecordsAbortController.abort();
    commitRecordsAbortController = null;
  }

  /**
   * 中止当前历史提交快照读取请求。
   *
   * 设计说明：切换模型的 OpenProject 阶段已经有自己的取消控制；这里负责更早的
   * snapshot/records 查询阶段，避免快速切换时旧 ListCommits 继续占用网络和后端资源。
   */
  function abortSwitchCommitRequest(): void {
    if (!switchCommitAbortController) {
      return;
    }
    switchCommitAbortController.abort();
    switchCommitAbortController = null;
  }

  /**
   * 判断历史预览切换请求是否仍是最新请求。
   *
   * 该保护用于阻止卸载后的晚到结果，或快速连续选择提交时的旧请求，继续重载模型、
   * 写入选中提交或触发提示。
   *
   * @param requestId 本次历史预览切换请求编号。
   * @returns 请求仍有效时返回 true。
   */
  function isActiveSwitchCommitRequest(requestId: number): boolean {
    return !isDisposed && switchCommitRequestId === requestId;
  }

  /**
   * 判断从历史提交创建工作区的请求是否仍是最新请求。
   *
   * 创建流程横跨创建、刷新记录与重载可编辑工作区三个异步边界，任一边界晚到都不能
   * 在卸载后或被新请求接管后回写 store。
   *
   * @param requestId 本次创建工作区请求编号。
   * @returns 请求仍有效时返回 true。
   */
  function isActiveCreateWorkspaceRequest(requestId: number): boolean {
    return !isDisposed && createWorkspaceRequestId === requestId;
  }

  /**
   * 清理历史版本切换防抖计时器。
   *
   * 版本列表允许用户快速扫选，计时器必须集中清理，避免旧计时器在较新选择后仍触发加载。
   */
  function clearSwitchDebounceTimer(): void {
    if (!switchDebounceTimer) {
      return;
    }
    clearTimeout(switchDebounceTimer);
    switchDebounceTimer = null;
  }

  /**
   * 结束当前被防抖合并的选择 Promise。
   *
   * 被后续选择覆盖的旧 `selectCommit()` 也需要被 resolve，否则调用方会一直等待一个不再执行的请求。
   */
  function resolveScheduledSwitchPromise(schedule: ScheduledSwitchCommit | null): void {
    schedule?.resolve();
    if (scheduledSwitchCommit === schedule) {
      scheduledSwitchCommit = null;
    }
  }

  /**
   * 恢复历史版本切换前的工作区模式。
   *
   * 设计说明：确认后立即进入只读预览可以让 UI 立刻显示 loading 和只读态，但模型加载失败时
   * 不能把用户留在一个并未真正切换成功的预览提交上，因此失败路径必须恢复进入前的模式。
   *
   * @param snapshot 切换前捕获的模式快照。
   */
  function restoreModeSnapshot(snapshot: VersionWorkspaceModeSnapshot): void {
    if (snapshot.mode === 'history-preview' && snapshot.selectedCommitId) {
      store.enterHistoryPreview(snapshot.selectedCommitId);
      return;
    }

    store.enterEditingMode();
  }

  /**
   * 判断目标提交是否已经是当前可编辑工作区基准。
   *
   * 设计说明：提交记录里的“当前编辑”是用户已打开模型的真实基线；点击它只是在列表中
   * 重新选择当前状态，不应触发历史预览确认或模型重载。
   *
   * @param commitId 用户点击的提交 ID。
   * @returns 编辑态且命中当前工作区提交时返回 true。
   */
  function isCurrentEditableWorkspaceCommit(commitId: string): boolean {
    const normalizedCommitId = commitId.trim();
    if (store.mode !== 'editing' || !normalizedCommitId) {
      return false;
    }

    return store.commitRecords.some((record) => (
      record.isCurrentWorkspaceCommit && record.id === normalizedCommitId
    ));
  }

  /**
   * 获取当前编辑态进入历史预览的风险确认结果。
   *
   * 设计说明：
   * 1. 快速点击多个提交时只弹一次确认，避免用户在同一次编辑态切换意图中被重复打断；
   * 2. 确认成功后，后续同一轮加载中的选择不再重复确认；
   * 3. 若加载失败仍停留在编辑态，则重置确认标记，下一次从编辑态切换仍会提示。
   *
   * @returns 用户确认继续切换时返回 true。
   */
  async function confirmEditingSwitchIfNeeded(): Promise<boolean> {
    if (store.mode !== 'editing' || hasConfirmedEditingSwitch) {
      return true;
    }

    if (!editingSwitchConfirmPromise) {
      editingSwitchConfirmPromise = confirmSwitchFromEditing();
      try {
        const confirmed = await editingSwitchConfirmPromise;
        if (confirmed) {
          hasConfirmedEditingSwitch = true;
        }
        return confirmed;
      } finally {
        if (editingSwitchConfirmPromise) {
          editingSwitchConfirmPromise = null;
        }
      }
    }

    const confirmed = await editingSwitchConfirmPromise;
    if (confirmed) {
      hasConfirmedEditingSwitch = true;
    }
    return confirmed;
  }

  /**
   * 调度历史提交切换。
   *
   * 选择提交记录采用 debounce + takeLatest：短时间内多次点击只加载最后一次；如果已有加载在跑，
   * 新选择会立即成为最新 request owner，旧请求晚到时只能自行结束，不能回写 store。
   *
   * @param commitId 用户最后选择的提交 ID。
   * @returns 当前调度批次完成后的 Promise。
   */
  function scheduleSwitchCommit(commitId: string): Promise<void> {
    pendingSwitchCommitId = commitId;
    clearSwitchDebounceTimer();

    if (scheduledSwitchCommit) {
      resolveScheduledSwitchPromise(scheduledSwitchCommit);
    }

    const requestId = switchCommitRequestId + 1;
    switchCommitRequestId = requestId;
    abortSwitchCommitRequest();
    let resolveSchedule!: () => void;
    const promise = new Promise<void>((resolve) => {
      resolveSchedule = resolve;
    });
    const schedule: ScheduledSwitchCommit = {
      commitId,
      requestId,
      resolve: resolveSchedule,
      promise,
    };
    scheduledSwitchCommit = schedule;
    store.beginSwitchingCommit(commitId);

    if (switchDebounceMs <= 0) {
      void flushPendingSwitchCommit(schedule);
    } else {
      switchDebounceTimer = setTimeout(() => {
        void flushPendingSwitchCommit(schedule);
      }, switchDebounceMs);
    }

    return promise;
  }

  /**
   * 立即执行一次历史提交切换。
   *
   * 设计说明：
   * 1. 该函数集中处理 fetch snapshot、模型重载、只读预览状态与失败恢复；
   * 2. 从编辑态确认后的切换会在拉取快照前进入只读预览态，保证确认后立刻出现 loading；
   * 3. 历史预览态内快速扫选仍可由外层防抖调度，只在模型真正成功后更新选中提交。
   *
   * @param commitId 目标历史提交 ID。
   * @param requestId 本次请求编号。
   * @param executeOptions 执行策略。
   */
  async function switchCommitNow(
    commitId: string,
    requestId: number,
    executeOptions: SwitchCommitNowOptions = {},
  ): Promise<void> {
    if (!commitId || !isActiveSwitchCommitRequest(requestId)
      || store.isCreatingWorkspace || store.mode === 'creating-workspace') {
      return;
    }

    store.beginSwitchingCommit(commitId);
    if (executeOptions.enterPreviewBeforeLoad) {
      store.enterHistoryPreview(commitId);
    }

    abortSwitchCommitRequest();
    const abortController = new AbortController();
    switchCommitAbortController = abortController;

    try {
      const snapshot = await service.fetchCommitSnapshot(getProjectId(), commitId, {
        signal: abortController.signal,
      });
      if (!isActiveSwitchCommitRequest(requestId)) {
        return;
      }
      await reloadCommitSnapshot(snapshot, {
        isCurrent: () => isActiveSwitchCommitRequest(requestId),
      });
      if (!isActiveSwitchCommitRequest(requestId)) {
        return;
      }
      if (!executeOptions.enterPreviewBeforeLoad) {
        store.enterHistoryPreview(commitId);
      }
    } catch (error) {
      if (!isActiveSwitchCommitRequest(requestId)) {
        return;
      }
      if (executeOptions.restoreStateOnFailure) {
        restoreModeSnapshot(executeOptions.restoreStateOnFailure);
      }
      hasConfirmedEditingSwitch = false;
      logger.error('[VersionWorkspace] switch commit preview failed', {
        projectId: getProjectId(),
        commitId,
        error,
      });
      notifyError('切换历史版本失败');
    } finally {
      if (switchCommitAbortController === abortController) {
        switchCommitAbortController = null;
      }
      if (isActiveSwitchCommitRequest(requestId)) {
        store.finishSwitchingCommit(commitId);
      }
    }
  }

  /**
   * 执行防抖后的最后一次历史提交切换。
   *
   * @param schedule 本次选择调度对象。
   */
  async function flushPendingSwitchCommit(schedule: ScheduledSwitchCommit): Promise<void> {
    const { commitId, requestId } = schedule;
    if (pendingSwitchCommitId === commitId) {
      pendingSwitchCommitId = null;
    }
    clearSwitchDebounceTimer();

    if (!commitId || !isActiveSwitchCommitRequest(requestId)
      || store.isCreatingWorkspace || store.mode === 'creating-workspace') {
      store.finishSwitchingCommit(commitId);
      resolveScheduledSwitchPromise(schedule);
      return;
    }

    try {
      await switchCommitNow(commitId, requestId);
    } finally {
      resolveScheduledSwitchPromise(schedule);
    }
  }

  /**
   * 加载当前项目提交记录。
   *
   * @returns 提交记录加载完成后的 Promise。
   */
  async function refreshCommitRecordsOrThrow(loadOptions: LoadCommitRecordsOptions = {}): Promise<boolean> {
    if (isDisposed) {
      return false;
    }

    const requestId = commitRecordsRequestId + 1;
    commitRecordsRequestId = requestId;
    abortCommitRecordsRequest();
    const abortController = new AbortController();
    commitRecordsAbortController = abortController;
    store.isRecordsLoading = true;
    store.recordsError = null;

    try {
      const requestOptions: FetchProjectCommitRecordsOptions = {
        signal: abortController.signal,
        ...(loadOptions.forceRefresh ? { forceRefresh: true } : {}),
      };
      const records = await service.fetchProjectCommitRecords(getProjectId(), requestOptions);
      if (!isActiveCommitRecordsRequest(requestId)) {
        return false;
      }
      store.setCommitRecords(records);
      return true;
    } catch (error) {
      if (!isActiveCommitRecordsRequest(requestId)) {
        return false;
      }
      logger.error('[VersionWorkspace] load commit records failed', error);
      store.recordsError = '加载提交记录失败';
      throw error;
    } finally {
      if (commitRecordsAbortController === abortController) {
        commitRecordsAbortController = null;
      }
      if (isActiveCommitRecordsRequest(requestId)) {
        store.isRecordsLoading = false;
      }
    }
  }

  async function loadCommitRecords(loadOptions: LoadCommitRecordsOptions = {}): Promise<void> {
    try {
      const refreshed = await refreshCommitRecordsOrThrow(loadOptions);
      if (!refreshed) {
        return;
      }
    } catch {
      if (isDisposed) {
        return;
      }
      // 首次打开项目时提交记录失败不应阻断模型首屏加载，因此公开加载入口只通知 UI 并吞掉异常。
      notifyError('加载提交记录失败');
    }
  }

  /**
   * 选择历史提交并进入只读预览。
   *
   * @param commitId 目标历史提交 ID。
   * @returns 切换完成后的 Promise。
   */
  async function selectCommit(commitId: string): Promise<void> {
    if (isDisposed || store.isCreatingWorkspace || store.mode === 'creating-workspace') {
      return;
    }
    if (isCurrentEditableWorkspaceCommit(commitId)) {
      return;
    }

    const previousModeSnapshot: VersionWorkspaceModeSnapshot = {
      mode: store.mode,
      selectedCommitId: store.selectedCommitId,
    };
    pendingSwitchCommitId = commitId;
    const confirmed = await confirmEditingSwitchIfNeeded();
    if (isDisposed || !confirmed || pendingSwitchCommitId !== commitId) {
      return;
    }

    if (previousModeSnapshot.mode === 'editing') {
      clearSwitchDebounceTimer();
      resolveScheduledSwitchPromise(scheduledSwitchCommit);
      pendingSwitchCommitId = null;
      const requestId = switchCommitRequestId + 1;
      switchCommitRequestId = requestId;
      abortSwitchCommitRequest();
      await switchCommitNow(commitId, requestId, {
        enterPreviewBeforeLoad: true,
        restoreStateOnFailure: previousModeSnapshot,
      });
      return;
    }

    await scheduleSwitchCommit(commitId);
  }

  /**
   * 基于历史提交创建新的可编辑工作区分支。
   *
   * @param commitId 派生工作区的来源提交 ID。
   * @param branchName 用户输入的工作区分支名称。
   * @returns 创建并重载成功时返回 true；被取消、失效或失败时返回 false。
   */
  async function createWorkspaceFromCommit(commitId: string, branchName: string): Promise<boolean> {
    if (store.isVersionSwitching || store.isCreatingWorkspace || store.mode === 'creating-workspace') {
      return false;
    }

    const requestId = createWorkspaceRequestId + 1;
    createWorkspaceRequestId = requestId;
    switchCommitRequestId += 1;
    abortSwitchCommitRequest();

    store.isCreatingWorkspace = true;
    store.mode = 'creating-workspace';

    try {
      const workspace = await service.createWorkspaceFromCommit(getProjectId(), commitId, branchName);
      if (!isActiveCreateWorkspaceRequest(requestId)) {
        return false;
      }
      await reloadEditableWorkspace(workspace);
      if (!isActiveCreateWorkspaceRequest(requestId)) {
        return false;
      }
      try {
        await refreshCommitRecordsOrThrow();
      } catch (refreshError) {
        if (!isActiveCreateWorkspaceRequest(requestId)) {
          return false;
        }
        logger.error('[VersionWorkspace] refresh commit records after workspace branch failed', {
          projectId: getProjectId(),
          commitId,
          branchName,
          error: refreshError,
        });
        notifyError('刷新提交记录失败');
      }
      if (!isActiveCreateWorkspaceRequest(requestId)) {
        return false;
      }
      store.enterEditingMode();
      hasConfirmedEditingSwitch = false;
      notifySuccess('已基于历史版本创建工作区分支');
      return true;
    } catch (error) {
      if (!isActiveCreateWorkspaceRequest(requestId)) {
        return false;
      }
      logger.error('[VersionWorkspace] create workspace from commit failed', {
        projectId: getProjectId(),
        commitId,
        branchName,
        error,
      });
      store.enterHistoryPreview(commitId);
      notifyError('创建工作区分支失败');
      return false;
    } finally {
      if (isActiveCreateWorkspaceRequest(requestId)) {
        store.isCreatingWorkspace = false;
      }
    }
  }

  /**
   * 完成继续编辑历史提交后的统一收口。
   *
   * 设计说明：继续编辑成功后，刷新页面必须仍然打开同一个提交。因此在切回编辑态前先同步
   * 外部可刷新入口；若同步失败，保留历史预览态，避免 UI 显示可编辑但 URL 仍指向旧提交。
   *
   * @param commitId 本次继续编辑使用的提交 ID。
   * @param requestId 本次继续编辑请求编号。
   * @returns 成功切回编辑态时返回 true；请求过期时返回 false。
   */
  async function finishContinueEditingFromCommit(commitId: string, requestId: number): Promise<boolean> {
    await syncEditableCommitRoute?.(commitId);
    if (!isActiveSwitchCommitRequest(requestId)) {
      return false;
    }

    store.markCurrentWorkspaceCommit(commitId);
    store.enterEditingMode();
    hasConfirmedEditingSwitch = false;
    return true;
  }

  /**
   * 基于历史提交加载模型并返回编辑态。
   *
   * 设计说明：本入口只改变前端当前加载的模型与只读状态，不创建分支、不刷新后端 HEAD，
   * 也不向用户展示或持久化编辑基准，避免制造后端暂不支持的版本血缘语义。
   *
   * @param commitId 需要加载为编辑态模型的历史提交 ID。
   * @returns 成功加载并返回编辑态时返回 true。
   */
  async function continueEditingFromCommit(commitId: string): Promise<boolean> {
    if (store.isVersionSwitching || store.isCreatingWorkspace || store.mode === 'creating-workspace') {
      return false;
    }

    const normalizedCommitId = commitId.trim();
    if (!normalizedCommitId) {
      return false;
    }

    clearSwitchDebounceTimer();
    resolveScheduledSwitchPromise(scheduledSwitchCommit);
    pendingSwitchCommitId = null;

    const requestId = switchCommitRequestId + 1;
    switchCommitRequestId = requestId;
    abortSwitchCommitRequest();

    let abortController: AbortController | null = null;

    try {
      if (store.mode === 'history-preview' && store.selectedCommitId === normalizedCommitId) {
        return await finishContinueEditingFromCommit(normalizedCommitId, requestId);
      }

      store.beginSwitchingCommit(normalizedCommitId);

      abortController = new AbortController();
      switchCommitAbortController = abortController;
      const snapshot = await service.fetchCommitSnapshot(getProjectId(), normalizedCommitId, {
        signal: abortController.signal,
      });
      if (!isActiveSwitchCommitRequest(requestId)) {
        return false;
      }
      await reloadCommitSnapshot(snapshot, {
        isCurrent: () => isActiveSwitchCommitRequest(requestId),
      });
      if (!isActiveSwitchCommitRequest(requestId)) {
        return false;
      }
      return await finishContinueEditingFromCommit(normalizedCommitId, requestId);
    } catch (error) {
      if (!isActiveSwitchCommitRequest(requestId)) {
        return false;
      }
      logger.error('[VersionWorkspace] continue editing from commit failed', {
        projectId: getProjectId(),
        commitId: normalizedCommitId,
        error,
      });
      notifyError('继续编辑历史版本失败');
      return false;
    } finally {
      if (abortController && switchCommitAbortController === abortController) {
        switchCommitAbortController = null;
      }
      if (isActiveSwitchCommitRequest(requestId)) {
        store.finishSwitchingCommit(normalizedCommitId);
      }
    }
  }

  /**
   * 释放 controller 生命周期，阻止晚到的提交记录请求继续回写 store。
   */
  function dispose(): void {
    isDisposed = true;
    clearSwitchDebounceTimer();
    pendingSwitchCommitId = null;
    commitRecordsRequestId += 1;
    abortCommitRecordsRequest();
    switchCommitRequestId += 1;
    abortSwitchCommitRequest();
    createWorkspaceRequestId += 1;
    resolveScheduledSwitchPromise(scheduledSwitchCommit);
    store.isRecordsLoading = false;
    store.finishSwitchingCommit();
    service.dispose?.();
  }

  return {
    loadCommitRecords,
    selectCommit,
    continueEditingFromCommit,
    createWorkspaceFromCommit,
    dispose,
  };
}
